// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Node-level event bus
 *
 * Simplified workspace-level events - all nodes auto-refresh on workspace:changed.
 * Node authors just call emitWorkspaceChange() after mutations.
 */

type NodeEventHandler = (payload?: unknown) => void

class NodeEventEmitter {
  private listeners: Map<string, Set<NodeEventHandler>> = new Map()

  subscribe(event: string, handler: NodeEventHandler): () => void {
    const normalized = event.trim()
    if (!this.listeners.has(normalized)) {
      this.listeners.set(normalized, new Set())
    }

    const bucket = this.listeners.get(normalized)!
    bucket.add(handler)

    return () => {
      bucket.delete(handler)
      if (bucket.size === 0) {
        this.listeners.delete(normalized)
      }
    }
  }

  emit(event: string, payload?: unknown): void {
    const normalized = event.trim()
    const bucket = this.listeners.get(normalized)
    if (!bucket) {
      return
    }

    bucket.forEach(handler => {
      try {
        handler(payload)
      } catch (error) {
        console.error('[node-events] handler error for', normalized, error)
      }
    })
  }
}

export const nodeEvents = new NodeEventEmitter()

/**
 * Emit workspace changed event - triggers auto-refresh of all nodes
 * Call this after any mutation that affects workspace data
 */
export function emitWorkspaceChange(payload?: { orgId?: string; wsId?: string; action?: string }): void {
  console.log('[node-events] workspace:changed', payload)
  nodeEvents.emit('workspace:changed', payload)
}
